import axios from "axios";
import { checkTokenExpiry } from "./checkTokenExpiry";
import { closeSnackbar } from 'notistack';
import { sessionDataDec } from "./sessionServiceEndDec";

const LOGIN_BASE_URL = import.meta.env.VITE_LOGIN_BASE_URL;
const AUTH_BASE_URL = import.meta.env.VITE_AUTH_BASE_URL;
const MW_BASE_URL = import.meta.env.VITE_MW_BASE_URL;
const ZENDESK_BASE_URL = import.meta.env.VITE_ZENDESK_BASE_URL;
const PINCODE_BASE_URL = import.meta.env.VITE_PINCODE_BASE_URL;
const HEADER_CHABI = import.meta.env.VITE_HEADER_CHABI;

// ==========================================
// CENTRALIZED PERFORMANCE HELPERS
// ==========================================

/**
 * Handles the session expiry check and extracts token 1-to-1 matching original logic
 */
const getValidToken = async () => {
  const valid = await checkTokenExpiry();
  if (!valid) return null;
  return sessionDataDec('token');
};

/**
 * Global response error filter maintaining exact functional return behavior
 */
const handleApiError = (err) => {
  if (err.response?.data?.error?.message?.valid === false) {
    closeSnackbar();
    window.dispatchEvent(new Event("session-expired"));
    return; // Returns undefined implicitly, exactly matching your original code
  }
  return Promise.reject(err);
};

// ==========================================
// EXPORTED ENDPOINTS
// ==========================================

export const LoginApi = (url, data) => {
  const client = axios.create({
    baseURL: LOGIN_BASE_URL,
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Authorization: HEADER_CHABI,
    },
  });

  return client
    .post(url, data)
    .then((res) => res.data)
    .catch((err) => Promise.reject(err));
};

export const LogOutApi = async (url, data) => {
  const token = await getValidToken();
  if (!token) return;

  const client = axios.create({
    baseURL: LOGIN_BASE_URL,
    headers: {
      "Content-Type": "application/json",
      Authorization: token,
    },
  });

  return client
    .post(url, data)
    .then((res) => res.data)
    .catch(handleApiError);
};

export const SessionDeleteApi = (url, data) => {
  const client = axios.create({
    baseURL: LOGIN_BASE_URL,
    headers: {
      "Content-Type": "application/json",
    },
  });

  return client
    .post(url, data)
    .then((res) => res.data)
    .catch((err) => Promise.reject(err));
};

export const RefreshApi = (url, data) => {
  const client = axios.create({
    baseURL: LOGIN_BASE_URL,
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      Authorization: HEADER_CHABI,
    },
  });

  return client
    .post(url, data)
    .then((res) => res.data)
    .catch((err) => Promise.reject(err));
};

export const AuthBaseGetApi = async (url) => {
  const token = await getValidToken();
  if (!token) return;

  const client = axios.create({
    baseURL: AUTH_BASE_URL,
    headers: {
      "Content-Type": "application/json",
      Authorization: token,
    },
  });

  return client
    .get(url)
    .then((res) => res.data)
    .catch(handleApiError);
};

export const AuthBasePostApi = async (url, data) => {
  const token = await getValidToken();
  if (!token) return;

  const client = axios.create({
    baseURL: AUTH_BASE_URL,
    headers: {
      "Content-Type": "application/json",
      Authorization: token,
    },
  });

  return client
    .post(url, data)
    .then((res) => res.data)
    .catch(handleApiError);
};

export const AuthBaseNoauthPostApi = async (url, data) => {
  const client = axios.create({
    baseURL: AUTH_BASE_URL,
    headers: {
      "Content-Type": "application/json",
    },
  });

  return client
    .post(url, data)
    .then((res) => res.data)
    .catch(handleApiError);
};

export const LoginGetApi = async (url) => {
  const token = await getValidToken();
  if (!token) return;

  const client = axios.create({
    baseURL: LOGIN_BASE_URL,
    headers: {
      "Content-Type": "application/json",
      Authorization: token,
    },
  });

  return client
    .get(url)
    .then((res) => res.data)
    .catch(handleApiError);
};

export const LoginPostApi = async (url, data) => {
  const token = await getValidToken();
  if (!token) return;

  const client = axios.create({
    baseURL: LOGIN_BASE_URL,
    headers: {
      "Content-Type": "application/json",
      Authorization: token,
    },
  });

  return client
    .post(url, data)
    .then((res) => res.data)
    .catch(handleApiError);
};

export const AuthBasePutApi = async (url, data) => {
  const token = await getValidToken();
  if (!token) return;

  const client = axios.create({
    baseURL: AUTH_BASE_URL,
    headers: {
      "Content-Type": "application/json",
      Authorization: token,
    },
  });

  return client
    .put(url, data)
    .then((res) => res.data)
    .catch(handleApiError);
};

export const MiddleWarePostApi = async (url, data, signal) => {
  const token = await getValidToken();
  if (!token) return;

  const client = axios.create({
    baseURL: MW_BASE_URL,
    headers: {
      "Content-Type": "application/json",
      Authorization: token,
    },
  });

  return client
    .post(url, data, signal ? { signal } : undefined)
    .then((res) => res.data)
    .catch(handleApiError);
};

export const MiddleWarePostExcelApi = async (url, data) => {
  const token = await getValidToken();
  if (!token) return;

  const response = await fetch(`${MW_BASE_URL}${url}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
    },
    body: JSON.stringify(data),
  }).catch((err) => Promise.reject(err));

  if (!response.ok) {
    const errData = await response.json().catch(() => ({}));
    if (errData?.error?.message?.valid === false) {
      closeSnackbar();
      window.dispatchEvent(new Event("session-expired"));
      return;
    }
    return Promise.reject({ response: { data: errData } });
  }

  return await response.arrayBuffer();
};

export const MiddleWareGetExcelApi = async (url) => {
  const token = await getValidToken();
  if (!token) return;

  const response = await fetch(`${MW_BASE_URL}${url}`, {
    method: 'GET',
    headers: {
      Authorization: token,
    },
  }).catch((err) => Promise.reject(err));

  if (!response.ok) {
    const errData = await response.json().catch(() => ({}));
    if (errData?.error?.message?.valid === false) {
      closeSnackbar();
      window.dispatchEvent(new Event("session-expired"));
      return;
    }
    return Promise.reject({ response: { data: errData } });
  }

  return await response.arrayBuffer();
};

export const MiddleWareGetApi = async (url) => {
  const token = await getValidToken();
  if (!token) return;

  const client = axios.create({
    baseURL: MW_BASE_URL,
    headers: {
      "Content-Type": "application/json",
      Authorization: token,
    },
  });

  return client
    .get(url)
    .then((res) => res.data)
    .catch(handleApiError);
};

// ADDED: Zendesk API call with clean, functionality-preserved optimization
export const ZendeskPostApi = async (url, data) => {
  const token = await getValidToken();
  if (!token) return;

  const client = axios.create({
    baseURL: ZENDESK_BASE_URL,
    headers: {
      "Content-Type": "application/json",
      Authorization: token,
    },
  });

  return client
    .post(url, data)
    .then((res) => res.data)
    .catch(handleApiError);
};

// ADDED: Pincode API call matching your precise base configuration structure
export const PinCodeApi = async (data) => {
  const client = axios.create({
    baseURL: PINCODE_BASE_URL,
  });

  return client
    .post('', data)
    .then((res) => res.data)
    .catch(handleApiError);
};
